// src/cockpit4/components/ExpertNetwork.tsx
// ============================================
// 专家网络与问答社区（知识中心 P2-功能9）- v2.0 详细改进计划
// 领域专家识别 / 内部问答社区 / 最佳实践分享 / 知识激励体系 / 专家预约
// 数据来源：localStorage（xeaos_expert_network）
// ============================================

import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { UserCheck, MessageSquare, Star, Award, CalendarClock, Send } from "lucide-react";

interface Expert {
  id: string;
  name: string;
  domain: string;
  title: string;
  level: number;
  answered: number;
  rating: number;
  available: boolean;
}

const INITIAL_EXPERTS: Expert[] = [
  { id: "e1", name: "夏老师", domain: "企业数字化", title: "数字化转型专家", level: 5, answered: 132, rating: 4.9, available: true },
  { id: "e2", name: "老周", domain: "经营管理", title: "连续创业者/CEO", level: 4, answered: 98, rating: 4.7, available: true },
  { id: "e3", name: "林工", domain: "AI 技术", title: "资深算法工程师", level: 5, answered: 156, rating: 4.8, available: false },
  { id: "e4", name: "陈总", domain: "市场营销", title: "品牌营销顾问", level: 3, answered: 64, rating: 4.5, available: true },
];

const QUESTIONS = [
  { id: "q1", title: "如何设计企业知识飞轮的启动路径？", author: "李女士", answers: 3, solved: true },
  { id: "q2", title: "OKR 与五层计划如何对齐执行？", author: "赵经理", answers: 2, solved: false },
  { id: "q3", title: "私域运营的复购提醒最佳频率？", author: "王先生", answers: 4, solved: true },
];

const KEY = "xeaos_expert_network";

function load(): Expert[] {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) return JSON.parse(raw) as Expert[];
  } catch { /* ignore */ }
  return INITIAL_EXPERTS;
}

export default function ExpertNetwork() {
  const [experts, setExperts] = useState<Expert[]>(load);
  const [qDraft, setQDraft] = useState("");

  const stats = useMemo(() => {
    const totalAnswers = experts.reduce((a, e) => a + e.answered, 0);
    const solved = QUESTIONS.filter((q) => q.solved).length;
    const avgRating = experts.reduce((a, e) => a + e.rating, 0) / experts.length;
    return { totalAnswers, solved, avgRating };
  }, [experts]);

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <UserCheck size={14} className="text-teal-400" />
        <h3 className="text-[12px] font-medium text-white">专家网络与问答社区</h3>
        <span className="text-[9px] font-mono text-gray-500 uppercase tracking-wider">P2-功能9 · Expert Network</span>
      </div>

      <div className="grid grid-cols-3 gap-2">
        <MetricCard label="专家解答" value={`${stats.totalAnswers} 次`} color="#34D399" />
        <MetricCard label="已解决问答" value={`${stats.solved}/${QUESTIONS.length}`} color="#38BDF8" />
        <MetricCard label="平均评分" value={`${stats.avgRating.toFixed(1)} / 5`} color="#FBBF24" />
      </div>

      <div className="grid grid-cols-12 gap-3">
        <div className="col-span-6 rounded-lg border border-white/[0.06] bg-white/[0.02] p-3">
          <div className="flex items-center gap-2 mb-2">
            <Award size={11} className="text-teal-400" />
            <span className="text-[10px] font-mono text-gray-400 uppercase tracking-wider">领域专家地图</span>
          </div>
          <div className="space-y-1.5">
            {experts.map((e) => (
              <div key={e.id} className="rounded-md border border-white/[0.04] bg-white/[0.01] p-2.5">
                <div className="flex items-center gap-2">
                  <span className="text-[10px] font-medium text-white">{e.name}</span>
                  <span className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-teal-500/15 text-teal-300">{e.domain}</span>
                  <span className="text-[8px] font-mono text-gray-600">{e.title}</span>
                  <span className={`ml-auto text-[9px] font-mono px-1.5 py-0.5 rounded ${e.available ? "bg-emerald-500/15 text-emerald-400" : "bg-gray-500/15 text-gray-500"}`}>{e.available ? "可预约" : "忙碌"}</span>
                </div>
                <div className="flex items-center gap-3 mt-1.5 text-[9px] font-mono text-gray-500">
                  <span className="flex items-center gap-0.5"><Star size={8} className="text-amber-400" /> {e.rating}</span>
                  <span>已答 {e.answered} 问</span>
                  <span className="flex items-center gap-0.5"><Award size={8} className="text-purple-400" /> Lv.{e.level}</span>
                  <button className="ml-auto flex items-center gap-1 px-1.5 py-0.5 rounded border border-teal-500/30 bg-teal-500/10 text-teal-300">
                    <CalendarClock size={8} /> 预约
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="col-span-6 rounded-lg border border-white/[0.06] bg-white/[0.02] p-3">
          <div className="flex items-center gap-2 mb-2">
            <MessageSquare size={11} className="text-sky-400" />
            <span className="text-[10px] font-mono text-gray-400 uppercase tracking-wider">内部问答社区 · AI 辅助回答</span>
          </div>
          <div className="space-y-1.5">
            {QUESTIONS.map((q) => (
              <div key={q.id} className="rounded-md border border-white/[0.04] bg-white/[0.01] px-2.5 py-2">
                <div className="flex items-center gap-2">
                  <span className="text-[10px] text-gray-300">{q.title}</span>
                  {q.solved && <span className="text-[8px] font-mono px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-400">已解决</span>}
                  <span className="ml-auto text-[8px] font-mono text-gray-600">{q.answers} 回答 · {q.author}</span>
                </div>
              </div>
            ))}
          </div>
          <div className="flex items-center gap-1.5 mt-2">
            <input
              value={qDraft}
              onChange={(e) => setQDraft(e.target.value)}
              placeholder="向社区提问，AI 与专家共同回答..."
              className="flex-1 rounded-md border border-white/[0.06] bg-black/20 px-2.5 py-1.5 text-[10px] font-mono text-gray-300 placeholder-gray-600 outline-none focus:border-teal-500/40"
            />
            <button className="flex items-center gap-1 px-2 py-1.5 rounded-md border border-teal-500/30 bg-teal-500/10 text-[9px] font-mono text-teal-300">
              <Send size={9} /> 提问
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function MetricCard({ label, value, color }: { label: string; value: string; color: string }) {
  return (
    <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="rounded-lg border border-white/[0.06] bg-white/[0.02] px-3 py-2">
      <div className="text-[9px] font-mono text-gray-500 uppercase">{label}</div>
      <div className="text-sm font-semibold font-mono mt-0.5" style={{ color }}>{value}</div>
    </motion.div>
  );
}

